import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/integrations/supabase/types';

type SocialPlatform = Database['public']['Enums']['social_platform'];

interface CreateRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platform: SocialPlatform;
  onRuleCreated: () => void;
}

const CreateRuleDialog = ({ open, onOpenChange, platform, onRuleCreated }: CreateRuleDialogProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  // rule_type values match the live CHECK constraint:
  //   listening → tag matches in the feed (don't hide them)
  //   filter    → tag matches AND hide them from the default feed view
  //   blocking  → same as filter; reserved for stricter future treatment
  const [formData, setFormData] = useState({
    name: '',
    ruleType: 'filter' as 'filter' | 'listening' | 'blocking',
    keywords: '',
    blockedUsers: '',
    blockedHashtags: '',
    sentimentFilter: '',
  });

  const [keywordTags, setKeywordTags] = useState<string[]>([]);
  const [userTags, setUserTags] = useState<string[]>([]);
  const [hashtagTags, setHashtagTags] = useState<string[]>([]);

  const handleAddTag = (type: 'keywords' | 'users' | 'hashtags', value: string) => {
    if (!value.trim()) return;
    
    const cleanValue = value.trim();
    switch (type) {
      case 'keywords':
        if (!keywordTags.includes(cleanValue)) {
          setKeywordTags([...keywordTags, cleanValue]);
          setFormData(prev => ({ ...prev, keywords: '' }));
        }
        break;
      case 'users':
        const username = cleanValue.startsWith('@') ? cleanValue.slice(1) : cleanValue;
        if (!userTags.includes(username)) {
          setUserTags([...userTags, username]);
          setFormData(prev => ({ ...prev, blockedUsers: '' }));
        }
        break;
      case 'hashtags':
        const hashtag = cleanValue.startsWith('#') ? cleanValue : `#${cleanValue}`;
        if (!hashtagTags.includes(hashtag)) {
          setHashtagTags([...hashtagTags, hashtag]);
          setFormData(prev => ({ ...prev, blockedHashtags: '' }));
        }
        break;
    }
  };

  const handleRemoveTag = (type: 'keywords' | 'users' | 'hashtags', index: number) => {
    switch (type) {
      case 'keywords':
        setKeywordTags(keywordTags.filter((_, i) => i !== index));
        break;
      case 'users':
        setUserTags(userTags.filter((_, i) => i !== index));
        break;
      case 'hashtags':
        setHashtagTags(hashtagTags.filter((_, i) => i !== index));
        break;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, type: 'keywords' | 'users' | 'hashtags') => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const value = (e.target as HTMLInputElement).value;
      handleAddTag(type, value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter a rule name');
      return;
    }

    if (keywordTags.length === 0 && userTags.length === 0 && hashtagTags.length === 0) {
      toast.error('Please add at least one keyword, user, or hashtag');
      return;
    }

    setLoading(true);
    
    try {
      const { error } = await supabase
        .from('filtering_rules')
        .insert({
          user_id: user?.id,
          name: formData.name.trim(),
          rule_type: formData.ruleType,
          platforms: [platform],
          keywords: keywordTags.length > 0 ? keywordTags : null,
          blocked_users: userTags.length > 0 ? userTags : null,
          blocked_hashtags: hashtagTags.length > 0 ? hashtagTags : null,
          sentiment_filter: formData.sentimentFilter || null,
          is_active: true
        });

      if (error) throw error;

      toast.success(
        formData.ruleType === 'listening'
          ? 'Listening rule created — matches will be tagged in your feed.'
          : 'Filter rule created — matches will be hidden from your feed.'
      );
      onRuleCreated();
      handleClose();
    } catch (error) {
      console.error('Error creating rule:', error);
      toast.error('Failed to create filtering rule');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      ruleType: 'filter',
      keywords: '',
      blockedUsers: '',
      blockedHashtags: '',
      sentimentFilter: '',
    });
    setKeywordTags([]);
    setUserTags([]);
    setHashtagTags([]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Content Rule</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="name">Rule Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="My brand monitoring rule"
              required
            />
          </div>

          <div>
            <Label>Rule Type</Label>
            <Select
              value={formData.ruleType}
              onValueChange={(value) => setFormData(prev => ({ ...prev, ruleType: value as 'filter' | 'listening' | 'blocking' }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select rule type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="filter">
                  Filter — hide matching content from the feed
                </SelectItem>
                <SelectItem value="listening">
                  Listening — tag matching content but keep it visible
                </SelectItem>
                <SelectItem value="blocking">
                  Blocking — same as Filter; reserved for stricter future handling
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground mt-1">
              {formData.ruleType === 'listening'
                ? 'Surface mentions, compliments, and topical discussions in your feed without hiding them.'
                : 'Hide spam, toxicity, and unwanted interactions from the default feed view.'
              }
            </p>
          </div>

          <div>
            <Label>
              {formData.ruleType === 'listening' ? 'Keywords to Find' : 'Keywords to Block'}
            </Label>
            <p className="text-sm text-muted-foreground mb-2">
              {formData.ruleType === 'listening'
                ? 'Brand names, product terms, positive words to monitor'
                : 'Spam keywords, offensive terms to block'
              }
            </p>
            <Input
              value={formData.keywords}
              onChange={(e) => setFormData(prev => ({ ...prev, keywords: e.target.value }))}
              onKeyDown={(e) => handleKeyPress(e, 'keywords')}
              placeholder={formData.ruleType === 'listening'
                ? 'your brand, amazing product, love it'
                : 'spam, toxic, inappropriate'
              }
            />
            {keywordTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {keywordTags.map((keyword, index) => (
                  <Badge
                    key={index}
                    variant={formData.ruleType === 'listening' ? 'default' : 'destructive'}
                    className="text-xs"
                  >
                    {keyword}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 ml-1"
                      onClick={() => handleRemoveTag('keywords', index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {(formData.ruleType === 'filter' || formData.ruleType === 'blocking') && (
            <>
              <div>
                <Label>Users to Block</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Specific accounts to block from your content stream
                </p>
                <Input
                  value={formData.blockedUsers}
                  onChange={(e) => setFormData(prev => ({ ...prev, blockedUsers: e.target.value }))}
                  onKeyDown={(e) => handleKeyPress(e, 'users')}
                  placeholder="spammer, trolluser"
                />
                {userTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {userTags.map((user, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        @{user}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 ml-1"
                          onClick={() => handleRemoveTag('users', index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label>Hashtags to Block</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Hashtags associated with spam or unwanted content
                </p>
                <Input
                  value={formData.blockedHashtags}
                  onChange={(e) => setFormData(prev => ({ ...prev, blockedHashtags: e.target.value }))}
                  onKeyDown={(e) => handleKeyPress(e, 'hashtags')}
                  placeholder="spam, hate, toxic"
                />
                {hashtagTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {hashtagTags.map((hashtag, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {hashtag}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 ml-1"
                          onClick={() => handleRemoveTag('hashtags', index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <div>
            <Label>Sentiment Focus</Label>
            <Select value={formData.sentimentFilter} onValueChange={(value) => setFormData(prev => ({ ...prev, sentimentFilter: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select sentiment preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All sentiment types</SelectItem>
                {formData.ruleType === 'listening' ? (
                  <>
                    <SelectItem value="positive">Focus on positive content</SelectItem>
                    <SelectItem value="neutral">Focus on neutral/informational content</SelectItem>
                    <SelectItem value="negative">Monitor negative feedback</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="negative">Block negative content</SelectItem>
                    <SelectItem value="positive">Block positive content</SelectItem>
                    <SelectItem value="neutral">Block neutral content</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground mt-1">
              {formData.ruleType === 'listening'
                ? 'AI will prioritize content matching your sentiment preference'
                : 'AI will block content matching the selected sentiment'
              }
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading
                ? 'Creating...'
                : formData.ruleType === 'listening'
                  ? 'Create Listening Rule'
                  : 'Create Filter Rule'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateRuleDialog;